import {AGE_BUCKETS, toAgeSeries} from "./customerDistribution";

/**
 * '설문조사 분석' 탭 — **설문 응답자**의 성별·연령대. (2026-10-06)
 *
 * 입력은 응답자별 시트(`GET /project/{id}/survey/responses`)의 행이다. 행마다 서버가 '고객 분석' 탭과
 * **같은 판정 함수**로 만든 `gender`(라벨: 여성/남성/미수집)와 `ageGroup`(구간 키: "20~24" 등)을 싣는다.
 *
 * 분포 계산도 '고객 분석'(`StatisticsService`)과 같게 맞춘다.
 *  - **값이 없는(null) 응답자는 분모에서 뺀다** — 서버가 `mapNotNull` 로 수집된 고객만 세는 것과 같다.
 *  - 퍼센트는 소수 2자리, 인원은 따로 센 값을 같이 준다(툴팁이 인원도 보여준다).
 *  - 성별 원형은 인원 많은 순, 연령대 막대는 축 순서(10대 → 60대) — 각각 '고객 분석' 차트와 같다.
 */

const toPercent = (count, total) => (total > 0 ? Number(((count / total) * 100).toFixed(2)) : 0);

const countBy = (values) => values.reduce((acc, value) => {
    acc[value] = (acc[value] ?? 0) + 1;
    return acc;
}, {});

/** 성별 원형 그래프 데이터 `[{name, value(%), count}]`. 수집된 응답자가 없으면 빈 배열. */
export const toRespondentGenderData = (rows) => {
    const genders = (rows ?? []).map(row => row?.gender).filter(gender => gender != null && gender !== "");
    const counts = countBy(genders);
    return Object.entries(counts)
        .sort((a, b) => b[1] - a[1])
        .map(([name, count]) => ({name, value: toPercent(count, genders.length), count}));
};

/** 연령대 막대 데이터 `{xAxis, series(%), counts}` — '고객 분석' 과 같은 축(`toAgeSeries`). */
export const toRespondentAgeSeries = (rows) => {
    const ages = (rows ?? []).map(row => row?.ageGroup).filter(age => age != null && age !== "");
    const counts = countBy(ages);
    const percents = Object.fromEntries(Object.entries(counts).map(([key, count]) => [key, toPercent(count, ages.length)]));
    return toAgeSeries(percents, counts);
};

const AGE_LABELS = Object.fromEntries(AGE_BUCKETS);

/** 시트 칸에 쓸 연령대 라벨. 서버 키 → '고객 분석' 축 라벨("20~24세"). 모르는 키는 그대로, 없으면 '미수집'. */
export const ageCellText = (ageGroup) => (ageGroup ? (AGE_LABELS[ageGroup] ?? ageGroup) : "미수집");

/** 시트 칸에 쓸 성별 라벨. 없으면 '미수집'(고객 분석 히트맵 필터와 같은 말). */
export const genderCellText = (gender) => gender || "미수집";
